import { type Data, Puck } from '@puckeditor/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useDetailTitle } from '@/components/page-header';
import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { getDesign, renameDesign, saveChrome } from '@/lib/designs';
import { fromPuckData, toPuckData } from '@/lib/page-document';
import { puckConfig } from '@/lib/puck-config';

/**
 * The chrome editor: the header and the footer of one design.
 *
 * Same editor and same document shape as a page -- these are section
 * documents too, stored on the design rather than on a page, and rendered
 * around every page by the storefront. Header and footer are edited one at a
 * time because Puck edits a single document, and switching remounts it.
 *
 * There is no draft here. A design that is not live is the draft; edits to
 * the live one are live, which is why the banner says so.
 */
export const Route = createFileRoute('/designs/$siteId')({
  component: DesignEditor,
});

type Slot = 'header' | 'footer';

function DesignEditor() {
  const { siteId } = useParams({ from: '/designs/$siteId' });
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canEdit = can('cms.manage');

  const [slot, setSlot] = useState<Slot>('header');

  const design = useQuery({
    queryKey: ['design', accountId, siteId],
    queryFn: () => getDesign(accountId, siteId),
    enabled: Boolean(accountId),
    // The editor owns the document once mounted; refetching under it would
    // discard in-progress edits.
    refetchOnWindowFocus: false,
  });

  useDetailTitle(design.data?.name ?? 'Design');

  if (design.isLoading) return <div className="text-muted-foreground">Loading editor…</div>;

  if (!design.data) {
    return (
      <EmptyState>
        No such design.{' '}
        <Link to="/designs" className="underline">
          Back to designs
        </Link>
      </EmptyState>
    );
  }

  return (
    <ChromeEditor
      key={`${siteId}:${slot}`}
      accountId={accountId}
      siteId={siteId}
      slot={slot}
      onSlotChange={setSlot}
      canEdit={canEdit}
      name={design.data.name}
      published={design.data.published}
      sections={slot === 'header' ? design.data.headerSections : design.data.footerSections}
      onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['design', accountId, siteId] });
        queryClient.invalidateQueries({ queryKey: ['designs', accountId] });
      }}
    />
  );
}

function ChromeEditor({
  accountId,
  siteId,
  slot,
  onSlotChange,
  canEdit,
  name,
  published,
  sections,
  onSaved,
}: {
  accountId: string;
  siteId: string;
  slot: Slot;
  onSlotChange: (slot: Slot) => void;
  canEdit: boolean;
  name: string;
  published: boolean;
  sections: unknown;
  onSaved: () => void;
}) {
  // Puck is uncontrolled once mounted, so the live document is tracked in a
  // ref: putting it in state would re-render the editor on every keystroke.
  const live = useRef<Data | null>(null);
  const [title, setTitle] = useState(name);

  const initialData = useMemo(() => toPuckData(sections), [sections]);

  const save = useMutation({
    mutationFn: async () => {
      const data = live.current;

      if (data) await saveChrome(accountId, siteId, slot, fromPuckData(data));
      if (title.trim() && title.trim() !== name)
        await renameDesign(accountId, siteId, title.trim());
    },
    onSuccess: () => {
      const label = slot === 'header' ? 'Header' : 'Footer';

      toast.success(published ? `${label} saved — it is live now.` : `${label} saved.`);
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleChange = useCallback((data: Data) => {
    live.current = data;
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/designs" className="text-muted-foreground text-sm hover:underline">
          ← Designs
        </Link>

        <Input
          className="h-8 w-56"
          value={title}
          disabled={!canEdit}
          onChange={(event) => setTitle(event.target.value)}
          aria-label="Design name"
        />

        {published && <Badge>Live</Badge>}

        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-md border p-0.5">
            {(['header', 'footer'] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={slot === option ? 'secondary' : 'ghost'}
                onClick={() => onSlotChange(option)}
              >
                {option === 'header' ? 'Header' : 'Footer'}
              </Button>
            ))}
          </div>

          <Button size="sm" onClick={() => save.mutate()} disabled={!canEdit || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {published && (
        <p className="text-muted-foreground text-xs">
          This design is live. Saving changes the storefront immediately — build a second design if
          you would rather work in private.
        </p>
      )}

      {/* Puck ships its own full-height chrome, so it gets an explicit box. */}
      <div className="h-[calc(100vh-13rem)] overflow-hidden rounded-lg border">
        <Puck
          config={puckConfig}
          data={initialData}
          onChange={handleChange}
          // Puck's own header carries a second Publish button; the bar above
          // already owns saving and the design's identity.
          overrides={{ header: () => <></> }}
        />
      </div>
    </div>
  );
}
